import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import { createStructuredSelector } from "reselect";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  documentId,
  doc,
  getDoc,
  updateDoc,
  limit,
  startAfter,
  Timestamp,
  and,
} from "firebase/firestore";
import { functions, database } from "../../firebase";
import { parseFunctionsResponse, serialize, deserialize } from "./utils";
import logger from "../../utils/logger";
import { sortBy } from "underscore";
import getSlug from "speakingurl";

const initialState = {
  updatingAllOrgLocations: false,
  updateAllOrgLocationsResult: null,
  updateAllOrgLocationsError: null,
  resyncingAllOrders: false,
  resyncAllOrdersResult: null,
  resyncAllOrdersError: null,
  backfillingAttendees: false,
  backfillAttendeesResult: null,
  backfillAttendeesError: null,
  organizations: {},
  organizationsLoading: false,
  organizationsFetched: false,
  users: {},
  usersLoading: false,
  usersFetched: false,
  allUsers: {},
  allUsersLoading: false,
  allUsersLastVisible: null,
  allUsersAllLoaded: false,
  allUsersSortBy: "createdAt",
  allUsersSortOrder: "desc",
  squareAccessTokens: {},
  squareAccessTokensLoading: false,
  squareAccessTokensFetched: false,
  organizationDetails: {},
  organizationDetailsLoading: {},
  organizationEvents: {},
  organizationEventsLoading: {},
  organizationEventStats: {},
  organizationEventsTruncated: {},
  organizationEventsTruncatedLoading: {},
  organizationEventStatsTruncated: {},
  organizationOrders: {},
  organizationOrdersLoading: {},
  organizationOrdersTruncated: {},
  organizationOrdersTruncatedLoading: {},
  organizationSquareLocations: {},
  organizationSquareLocationsLoading: {},
  organizationSquareTokens: {},
  organizationSquareTokensLoading: {},
  crmStats: {},
  crmStatsLoading: false,
  userDetails: {},
  userDetailsLoading: {},
  userEvents: {},
  userEventsLoading: {},
  userOrders: {},
  userOrdersLoading: {},
  allEvents: {},
  allEventsLoading: false,
  allEventsLastVisible: null,
  allEventsAllLoaded: false,
  allEventsFilter: "upcoming",
  allOrders: {},
  allOrdersLoading: false,
  allOrdersLastVisible: null,
  allOrdersAllLoaded: false,
  allOrdersSortBy: "createdAt",
  allOrdersSortOrder: "desc",
};

const crmSlice = createSlice({
  name: "crm",
  initialState,
  reducers: {
    clearUpdateAllOrgLocationsResult(state) {
      state.updateAllOrgLocationsResult = null;
      state.updateAllOrgLocationsError = null;
    },
    clearResyncAllOrdersResult(state) {
      state.resyncAllOrdersResult = null;
      state.resyncAllOrdersError = null;
    },
    clearBackfillAttendeesResult(state) {
      state.backfillAttendeesResult = null;
      state.backfillAttendeesError = null;
    },
    updateEventOverrides(state, action) {
      const { eventId, hideFromPublicLists, hideEvent } = action.payload;
      if (state.allEvents[eventId]) {
        // Events are stored serialized in state, so we update the serialized version
        const event = { ...state.allEvents[eventId] };
        // Update override fields
        if (hideFromPublicLists !== undefined) {
          event.hideFromPublicLists = hideFromPublicLists;
        }
        if (hideEvent !== undefined) {
          event.hideEvent = hideEvent;
        }
        // Update the event in state
        state.allEvents[eventId] = event;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateAllOrgLocations.pending, (state) => {
        state.updatingAllOrgLocations = true;
        state.updateAllOrgLocationsResult = null;
        state.updateAllOrgLocationsError = null;
      })
      .addCase(updateAllOrgLocations.fulfilled, (state, action) => {
        state.updatingAllOrgLocations = false;
        if (action.payload) {
          state.updateAllOrgLocationsResult = action.payload;
        }
      })
      .addCase(updateAllOrgLocations.rejected, (state, action) => {
        state.updatingAllOrgLocations = false;
        state.updateAllOrgLocationsError =
          action.error.message || "An error occurred";
      })
      .addCase(fetchAllOrganizations.pending, (state) => {
        state.organizationsLoading = true;
      })
      .addCase(fetchAllOrganizations.fulfilled, (state, action) => {
        state.organizationsLoading = false;
        state.organizationsFetched = true;
        if (action.payload) {
          const { organizations, users } = action.payload;
          state.organizations = organizations || {};
          state.users = {
            ...state.users,
            ...(users || {}),
          };
        }
      })
      .addCase(fetchAllOrganizations.rejected, (state) => {
        state.organizationsLoading = false;
        state.organizationsFetched = true;
      })
      .addCase(fetchAllUsers.pending, (state) => {
        state.usersLoading = true;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.usersFetched = true;
        if (action.payload) {
          state.users = {
            ...state.users,
            ...(action.payload || {}),
          };
        }
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        state.usersLoading = false;
        state.usersFetched = true;
      })
      .addCase(fetchAllUsersPaginated.pending, (state) => {
        state.allUsersLoading = true;
      })
      .addCase(fetchAllUsersPaginated.fulfilled, (state, action) => {
        state.allUsersLoading = false;
        if (action.payload) {
          const { users, lastVisible, allUsersLoaded, sortBy, sortOrder } =
            action.payload;
          const reset = action.meta?.arg?.reset;
          if (reset) {
            state.allUsers = users || {};
            state.allUsersLastVisible = null;
            state.allUsersAllLoaded = false;
          } else if (users) {
            state.allUsers = { ...state.allUsers, ...users };
          }
          state.allUsersLastVisible = lastVisible;
          state.allUsersAllLoaded = allUsersLoaded;
          if (sortBy !== undefined) {
            state.allUsersSortBy = sortBy;
          }
          if (sortOrder !== undefined) {
            state.allUsersSortOrder = sortOrder;
          }
        }
      })
      .addCase(fetchAllUsersPaginated.rejected, (state) => {
        state.allUsersLoading = false;
      })
      .addCase(fetchAllSquareAccessTokens.pending, (state) => {
        state.squareAccessTokensLoading = true;
      })
      .addCase(fetchAllSquareAccessTokens.fulfilled, (state, action) => {
        state.squareAccessTokensLoading = false;
        state.squareAccessTokensFetched = true;
        if (action.payload) {
          state.squareAccessTokens = action.payload || {};
        }
      })
      .addCase(fetchAllSquareAccessTokens.rejected, (state) => {
        state.squareAccessTokensLoading = false;
        state.squareAccessTokensFetched = true;
      })
      .addCase(fetchCrmStats.pending, (state) => {
        state.crmStatsLoading = true;
      })
      .addCase(fetchCrmStats.fulfilled, (state, action) => {
        state.crmStatsLoading = false;
        if (action.payload) {
          state.crmStats = action.payload;
        }
      })
      .addCase(fetchCrmStats.rejected, (state) => {
        state.crmStatsLoading = false;
      })
      .addCase(fetchOrganizationDetails.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationDetailsLoading[organizationId] = true;
      })
      .addCase(fetchOrganizationDetails.fulfilled, (state, action) => {
        const { organizationId, organization, users } = action.payload;
        state.organizationDetailsLoading[organizationId] = false;
        state.organizationDetails[organizationId] = organization;
        // Merge fetched users into the users state (users are already serialized from thunk)
        if (users) {
          state.users = {
            ...state.users,
            ...users,
          };
        }
      })
      .addCase(fetchOrganizationDetails.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationDetailsLoading[organizationId] = false;
      })
      .addCase(fetchOrganizationEvents.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationEventsLoading[organizationId] = true;
      })
      .addCase(fetchOrganizationEvents.fulfilled, (state, action) => {
        const { organizationId, events, eventStats } = action.payload; // Payload is already serialized
        state.organizationEventsLoading[organizationId] = false;
        state.organizationEvents[organizationId] = events;
        if (eventStats) {
          state.organizationEventStats[organizationId] = eventStats;
        }
      })
      .addCase(fetchOrganizationEvents.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationEventsLoading[organizationId] = false;
      })
      .addCase(fetchOrganizationEventsTruncated.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationEventsTruncatedLoading[organizationId] = true;
      })
      .addCase(fetchOrganizationEventsTruncated.fulfilled, (state, action) => {
        const { organizationId, events, eventStats } = action.payload; // Payload is already serialized
        state.organizationEventsTruncatedLoading[organizationId] = false;
        state.organizationEventsTruncated[organizationId] = events;
        if (eventStats) {
          state.organizationEventStatsTruncated[organizationId] = eventStats;
        }
      })
      .addCase(fetchOrganizationEventsTruncated.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationEventsTruncatedLoading[organizationId] = false;
      })
      .addCase(fetchOrganizationOrdersTruncated.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationOrdersTruncatedLoading[organizationId] = true;
      })
      .addCase(fetchOrganizationOrdersTruncated.fulfilled, (state, action) => {
        const { organizationId, orders } = action.payload; // Payload is already serialized
        state.organizationOrdersTruncatedLoading[organizationId] = false;
        state.organizationOrdersTruncated[organizationId] = orders;
      })
      .addCase(fetchOrganizationOrdersTruncated.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationOrdersTruncatedLoading[organizationId] = false;
      })
      .addCase(fetchOrganizationOrders.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationOrdersLoading[organizationId] = true;
      })
      .addCase(fetchOrganizationOrders.fulfilled, (state, action) => {
        const { organizationId, orders } = action.payload;
        state.organizationOrdersLoading[organizationId] = false;
        state.organizationOrders[organizationId] = orders;
      })
      .addCase(fetchOrganizationOrders.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationOrdersLoading[organizationId] = false;
      })
      .addCase(fetchOrganizationSquareLocations.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationSquareLocationsLoading[organizationId] = true;
      })
      .addCase(fetchOrganizationSquareLocations.fulfilled, (state, action) => {
        const { organizationId, locations } = action.payload;
        state.organizationSquareLocationsLoading[organizationId] = false;
        state.organizationSquareLocations[organizationId] = locations;
      })
      .addCase(fetchOrganizationSquareLocations.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationSquareLocationsLoading[organizationId] = false;
      })
      .addCase(fetchOrganizationSquareTokens.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationSquareTokensLoading[organizationId] = true;
      })
      .addCase(fetchOrganizationSquareTokens.fulfilled, (state, action) => {
        const { organizationId, tokens } = action.payload;
        state.organizationSquareTokensLoading[organizationId] = false;
        state.organizationSquareTokens[organizationId] = tokens;
      })
      .addCase(fetchOrganizationSquareTokens.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        state.organizationSquareTokensLoading[organizationId] = false;
      })
      .addCase(fetchUserDetails.pending, (state, action) => {
        const { userId } = action.meta.arg;
        state.userDetailsLoading[userId] = true;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        const { userId, user } = action.payload;
        state.userDetailsLoading[userId] = false;
        state.userDetails[userId] = user;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        const { userId } = action.meta.arg;
        state.userDetailsLoading[userId] = false;
      })
      .addCase(fetchUserEvents.pending, (state, action) => {
        const { userId } = action.meta.arg;
        state.userEventsLoading[userId] = true;
      })
      .addCase(fetchUserEvents.fulfilled, (state, action) => {
        const { userId, events, eventStats } = action.payload;
        state.userEventsLoading[userId] = false;
        state.userEvents[userId] = events;
        if (eventStats) {
          // Store event stats keyed by userId for easy lookup
          if (!state.userEventStats) {
            state.userEventStats = {};
          }
          state.userEventStats[userId] = eventStats;
        }
      })
      .addCase(fetchUserEvents.rejected, (state, action) => {
        const { userId } = action.meta.arg;
        state.userEventsLoading[userId] = false;
      })
      .addCase(updateUserSuperAdmin.pending, (state, action) => {
        const { userId } = action.meta.arg;
        state.userDetailsLoading[userId] = true;
      })
      .addCase(updateUserSuperAdmin.fulfilled, (state, action) => {
        const { userId, user } = action.payload;
        state.userDetailsLoading[userId] = false;
        state.userDetails[userId] = user;
      })
      .addCase(updateUserSuperAdmin.rejected, (state, action) => {
        const { userId } = action.meta.arg;
        state.userDetailsLoading[userId] = false;
      })
      .addCase(fetchAllEvents.pending, (state, action) => {
        const { filter, reset } = action.meta.arg || {};
        if (reset || state.allEventsFilter !== filter) {
          state.allEvents = {};
          state.allEventsLastVisible = null;
          state.allEventsAllLoaded = false;
          state.allEventsFilter = filter || "upcoming";
        }
        state.allEventsLoading = true;
      })
      .addCase(fetchAllEvents.fulfilled, (state, action) => {
        state.allEventsLoading = false;
        if (action.payload) {
          const { events, eventStats, lastVisible, allEventsLoaded, filter } =
            action.payload;
          // Store serialized events in state (events are already serialized from thunk)
          // Merge new events with existing ones
          state.allEvents = {
            ...state.allEvents,
            ...events,
          };

          // Merge event stats (already serialized)
          if (!state.allEventStats) {
            state.allEventStats = {};
          }
          state.allEventStats = {
            ...state.allEventStats,
            ...eventStats,
          };

          state.allEventsLastVisible = lastVisible;
          state.allEventsAllLoaded = allEventsLoaded;
          state.allEventsFilter = filter;
        }
      })
      .addCase(fetchAllEvents.rejected, (state) => {
        state.allEventsLoading = false;
      })
      .addCase(resyncAllOrders.pending, (state) => {
        state.resyncingAllOrders = true;
        state.resyncAllOrdersResult = null;
        state.resyncAllOrdersError = null;
      })
      .addCase(resyncAllOrders.fulfilled, (state, action) => {
        state.resyncingAllOrders = false;
        state.resyncAllOrdersResult = action.payload;
        // Clear orders cache so CRM Orders list refetches and shows new purchaser data
        state.allOrders = {};
        state.allOrdersLastVisible = null;
        state.allOrdersAllLoaded = false;
      })
      .addCase(resyncAllOrders.rejected, (state, action) => {
        state.resyncingAllOrders = false;
        state.resyncAllOrdersError =
          action.error.message || "An error occurred";
      })
      .addCase(backfillAttendeesForOrganization.pending, (state) => {
        state.backfillingAttendees = true;
        state.backfillAttendeesResult = null;
        state.backfillAttendeesError = null;
      })
      .addCase(backfillAttendeesForOrganization.fulfilled, (state, action) => {
        state.backfillingAttendees = false;
        state.backfillAttendeesResult = action.payload;
      })
      .addCase(backfillAttendeesForOrganization.rejected, (state, action) => {
        state.backfillingAttendees = false;
        state.backfillAttendeesError =
          action.error?.message || "An error occurred";
      })
      .addCase(fetchAllOrders.pending, (state) => {
        state.allOrdersLoading = true;
      })
      .addCase(fetchAllOrders.fulfilled, (state, action) => {
        state.allOrdersLoading = false;
        if (action.payload) {
          const { orders, lastVisible, allOrdersLoaded } = action.payload;
          const { sortBy, sortOrder } = action.meta.arg || {};
          // Update sort state if provided
          if (sortBy !== undefined) {
            state.allOrdersSortBy = sortBy;
          }
          if (sortOrder !== undefined) {
            state.allOrdersSortOrder = sortOrder;
          }
          // Store serialized orders in state (orders are already serialized from thunk)
          if (action.meta.arg?.reset) {
            state.allOrders = orders || {};
            state.allOrdersLastVisible = lastVisible;
            state.allOrdersAllLoaded = allOrdersLoaded;
          } else {
            // Merge new orders with existing ones
            state.allOrders = {
              ...state.allOrders,
              ...(orders || {}),
            };
            state.allOrdersLastVisible = lastVisible;
            state.allOrdersAllLoaded = allOrdersLoaded;
          }
        }
      })
      .addCase(fetchAllOrders.rejected, (state) => {
        state.allOrdersLoading = false;
      });
  },
});

export const updateAllOrgLocations = createAsyncThunk(
  "crm/updateAllOrgLocations",
  async (params, { getState, dispatch }) => {
    const { locationOverrides = {} } = params || {};
    try {
      logger.info("Updating all organization locations");
      const updateAllOrgLocationsFunction = httpsCallable(
        functions,
        "updateAllOrgLocationsScheduleManual",
      );
      const result = await updateAllOrgLocationsFunction({
        locationOverrides,
      });
      const response = parseFunctionsResponse(result.data);
      logger.info("Update all organization locations completed", response);
      return response;
    } catch (e) {
      logger.error("error updating all organization locations", e);
      throw e;
    }
  },
);

export const resyncAllOrders = createAsyncThunk(
  "crm/resyncAllOrders",
  async (params, { getState, dispatch }) => {
    try {
      logger.info("Resyncing all orders");
      const resyncAllOrdersFunction = httpsCallable(
        functions,
        "resyncAllOrders",
      );
      const result = await resyncAllOrdersFunction({});
      const response = parseFunctionsResponse(result.data);
      logger.info("Resync all orders completed", response);
      return response;
    } catch (e) {
      logger.error("error resyncing all orders", e);
      throw e;
    }
  },
);

export const backfillAttendeesForOrganization = createAsyncThunk(
  "crm/backfillAttendeesForOrganization",
  async ({ organizationId }, { getState, dispatch }) => {
    try {
      logger.info("Backfilling attendees for organization", organizationId);
      const fn = httpsCallable(functions, "backfillAttendeesForOrganization");
      const result = await fn({ organizationId });
      const response = parseFunctionsResponse(result.data);
      logger.info("Backfill attendees completed", response);
      return response;
    } catch (e) {
      logger.error("error backfilling attendees", e, { organizationId });
      throw e;
    }
  },
);

export const fetchAllOrganizations = createAsyncThunk(
  "crm/fetchAllOrganizations",
  async (params, { getState, dispatch }) => {
    try {
      logger.info("Fetching all organizations");
      const organizationsRef = collection(database, "organizations");
      const q = query(organizationsRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const organizations = querySnapshot.docs.reduce((acc, doc) => {
        return {
          ...acc,
          [doc.id]: { id: doc.id, ...doc.data() },
        };
      }, {});

      // Collect all unique user IDs from organizations
      const userIdsSet = new Set();
      Object.values(organizations).forEach((org) => {
        if (org.owner) {
          userIdsSet.add(org.owner);
        }
        if (org.admins && Array.isArray(org.admins)) {
          org.admins.forEach((adminId) => userIdsSet.add(adminId));
        }
        if (org.scanners && Array.isArray(org.scanners)) {
          org.scanners.forEach((scannerId) => userIdsSet.add(scannerId));
        }
      });

      const allUserIds = Array.from(userIdsSet);

      // Check which users are already in the store
      const state = getState();
      const crmState = state.crm;
      const existingUsers = crmState.users || {};
      const existingUserIds = new Set(Object.keys(existingUsers));

      // Filter out users we already have
      const userIdsToFetch = allUserIds.filter(
        (id) => !existingUserIds.has(id),
      );

      logger.info("Fetching users", {
        total: allUserIds.length,
        existing: existingUserIds.size,
        toFetch: userIdsToFetch.length,
      });

      // Fetch users in batches (Firestore IN query limit is 10)
      const IN_QUERY_LIMIT = 10;
      const users = { ...existingUsers };

      if (userIdsToFetch.length > 0) {
        for (let i = 0; i < userIdsToFetch.length; i += IN_QUERY_LIMIT) {
          const batch = userIdsToFetch.slice(i, i + IN_QUERY_LIMIT);
          const usersRef = collection(database, "users");
          const userQuery = query(usersRef, where(documentId(), "in", batch));
          const userSnapshot = await getDocs(userQuery);
          userSnapshot.docs.forEach((doc) => {
            users[doc.id] = { id: doc.id, ...doc.data() };
          });
        }
      }

      return serialize({
        organizations,
        users,
      });
    } catch (e) {
      logger.error("error fetching all organizations", e);
      throw e;
    }
  },
);

const updatingAllOrgLocationsSelector = (state) =>
  state.crm.updatingAllOrgLocations;
const updateAllOrgLocationsResultSelector = (state) =>
  state.crm.updateAllOrgLocationsResult;
const updateAllOrgLocationsErrorSelector = (state) =>
  state.crm.updateAllOrgLocationsError;
const resyncingAllOrdersSelector = (state) => state.crm.resyncingAllOrders;
const resyncAllOrdersResultSelector = (state) =>
  state.crm.resyncAllOrdersResult;
const resyncAllOrdersErrorSelector = (state) => state.crm.resyncAllOrdersError;
const backfillingAttendeesSelector = (state) =>
  state.crm.backfillingAttendees;
const backfillAttendeesResultSelector = (state) =>
  state.crm.backfillAttendeesResult;
const backfillAttendeesErrorSelector = (state) =>
  state.crm.backfillAttendeesError;
const organizationsRawSelector = (state) => state.crm.organizations;
const organizationsLoadingSelector = (state) => state.crm.organizationsLoading;
const organizationsFetchedSelector = (state) => state.crm.organizationsFetched;
const usersRawSelector = (state) => state.crm.users;
const usersLoadingSelector = (state) => state.crm.usersLoading;
const usersFetchedSelector = (state) => state.crm.usersFetched;
const squareAccessTokensRawSelector = (state) => state.crm.squareAccessTokens;
const squareAccessTokensLoadingSelector = (state) =>
  state.crm.squareAccessTokensLoading;
const squareAccessTokensFetchedSelector = (state) =>
  state.crm.squareAccessTokensFetched;

// Deserializing selectors
const organizationsSelector = createSelector(
  [organizationsRawSelector],
  (organizations) => deserialize(organizations || {}),
);

const usersSelector = createSelector([usersRawSelector], (users) =>
  deserialize(users || {}),
);

const organizationsListSelector = createSelector(
  [organizationsSelector],
  (organizations) => {
    const organizationList = Object.keys(organizations || {}).map(
      (key) => organizations[key],
    );
    return sortBy(organizationList, (org) => {
      return org.createdAt?.toMillis?.() || 0;
    });
  },
);

const usersListSelector = createSelector(
  [usersSelector, organizationsSelector],
  (users, organizations) => {
    const userList = Object.keys(users || {}).map((key) => {
      const user = users[key];
      const userId = user.id;

      // Find all organizations this user is part of and their roles
      const orgMemberships = [];
      Object.values(organizations || {}).forEach((org) => {
        const roles = [];
        if (org.owner === userId) {
          roles.push("Owner");
        }
        if (
          org.admins &&
          Array.isArray(org.admins) &&
          org.admins.includes(userId)
        ) {
          roles.push("Admin");
        }
        if (
          org.scanners &&
          Array.isArray(org.scanners) &&
          org.scanners.includes(userId)
        ) {
          roles.push("Scanner");
        }

        if (roles.length > 0) {
          orgMemberships.push({
            organizationId: org.id,
            organizationName: org.name || "Unnamed Organization",
            roles,
          });
        }
      });

      return {
        ...user,
        organizations: orgMemberships,
      };
    });

    return sortBy(userList, (user) => {
      return user.createdAt?.toMillis?.() || 0;
    });
  },
);

// Deserializing selector for squareAccessTokens
const squareAccessTokensSelector = createSelector(
  [squareAccessTokensRawSelector],
  (tokens) => deserialize(tokens || {}),
);

const squareAccessTokensListSelector = createSelector(
  [squareAccessTokensSelector, organizationsSelector],
  (tokens, organizations) => {
    const tokenList = Object.keys(tokens || {}).map((key) => {
      const token = tokens[key];
      const org = organizations[token.organizationId];
      return {
        ...token,
        organizationName: org?.name || "Unknown Organization",
      };
    });

    // Sort so ACTIVE status is first, then by createdAt (descending)
    return tokenList.sort((a, b) => {
      // ACTIVE status comes first
      if (a.status === "ACTIVE" && b.status !== "ACTIVE") return -1;
      if (a.status !== "ACTIVE" && b.status === "ACTIVE") return 1;

      // Then sort by createdAt descending
      const aCreated = a.createdAt?.toMillis?.() || 0;
      const bCreated = b.createdAt?.toMillis?.() || 0;
      return bCreated - aCreated;
    });
  },
);

export const fetchAllUsers = createAsyncThunk(
  "crm/fetchAllUsers",
  async (params, { getState, dispatch }) => {
    try {
      logger.info("Fetching all users");
      const usersRef = collection(database, "users");
      const q = query(usersRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs.reduce((acc, doc) => {
        return {
          ...acc,
          [doc.id]: { id: doc.id, ...doc.data() },
        };
      }, {});

      // Check which users are already in the store
      const state = getState();
      const crmState = state.crm;
      const existingUsers = crmState.users || {};

      // Merge with existing users (don't overwrite if already fetched)
      const mergedUsers = { ...existingUsers, ...users };

      return serialize(mergedUsers);
    } catch (e) {
      logger.error("error fetching all users", e);
      throw e;
    }
  },
);

export const fetchAllUsersPaginated = createAsyncThunk(
  "crm/fetchAllUsersPaginated",
  async (params, { getState, dispatch }) => {
    try {
      const {
        pageSize = 50,
        lastVisible,
        reset = false,
        sortBy,
        sortOrder,
        filters = { include: {}, exclude: {} },
      } = params || {};
      logger.info("Fetching all users (paginated)", {
        pageSize,
        lastVisible,
        reset,
        sortBy,
        sortOrder,
        filters,
      });

      const state = getState();
      const crmState = state.crm;

      // Use provided sort params or fall back to state
      const currentSortBy =
        sortBy !== undefined ? sortBy : crmState.allUsersSortBy || "createdAt";
      const currentSortOrder =
        sortOrder !== undefined
          ? sortOrder
          : crmState.allUsersSortOrder || "desc";

      // If reset, start fresh
      if (reset) {
        // Will fetch from beginning
      } else if (!reset && crmState.allUsersAllLoaded) {
        logger.info("All users already loaded");
        return null;
      }

      const usersRef = collection(database, "users");

      // Build where conditions
      const whereConditions = [];

      // Add include filters (field == value1 OR field == value2)
      // Note: superAdmin and organizationId filters are handled client-side
      Object.entries(filters.include || {}).forEach(([field, values]) => {
        if (values && values.length > 0) {
          // Skip superAdmin and organizationId - handle client-side
          if (field === "superAdmin" || field === "organizationId") {
            return;
          }

          // Firestore doesn't support OR directly, so we use 'in' operator
          if (values.length === 1) {
            whereConditions.push(where(field, "==", values[0]));
          } else if (values.length <= 10) {
            whereConditions.push(where(field, "in", values));
          } else {
            // Fallback: use first 10
            whereConditions.push(where(field, "in", values.slice(0, 10)));
          }
        }
      });

      let usersQuery = query(
        usersRef,
        ...whereConditions,
        orderBy(currentSortBy, currentSortOrder),
        limit(pageSize),
      );

      if (lastVisible && !reset) {
        // Get the last visible value for the current sort field
        const lastVisibleValue = lastVisible[currentSortBy];
        if (lastVisibleValue !== undefined && lastVisibleValue !== null) {
          let startAfterValue = lastVisibleValue;

          // Convert timestamp if needed
          if (
            (currentSortBy === "createdAt" || currentSortBy === "lastLogin") &&
            lastVisibleValue.seconds !== undefined
          ) {
            startAfterValue = new Timestamp(
              lastVisibleValue.seconds,
              lastVisibleValue.nanoseconds || 0,
            );
          }

          usersQuery = query(
            usersRef,
            ...whereConditions,
            orderBy(currentSortBy, currentSortOrder),
            startAfter(startAfterValue),
            limit(pageSize),
          );
        }
      }

      const usersSnapshot = await getDocs(usersQuery);
      let usersArray = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Apply exclude filters client-side (Firestore limitation with != and orderBy)
      // Also apply include filters for superAdmin and organizationId client-side
      let visibleUsersArray = usersArray;
      const hasExcludeFilters =
        filters.exclude && Object.keys(filters.exclude).length > 0;
      const hasSuperAdminIncludeFilter =
        filters.include?.superAdmin?.length > 0;
      const hasOrganizationIncludeFilter =
        filters.include?.organizationId?.length > 0;

      // Get organizations from state to check membership
      const currentState = getState();
      const currentCrmState = currentState.crm;
      // Use organizationsRaw and deserialize it
      const organizationsRaw = currentCrmState.organizationsRaw || {};
      const organizations = deserialize(organizationsRaw);

      // Apply organizationId include filter client-side (check membership)
      if (hasOrganizationIncludeFilter) {
        const organizationIds = filters.include.organizationId;
        visibleUsersArray = visibleUsersArray.filter((user) => {
          const userId = user.id;
          // Check if user is a member of any of the filtered organizations
          return organizationIds.some((orgId) => {
            const org = organizations[orgId];
            if (!org) return false;
            return (
              org.owner === userId ||
              (org.admins && org.admins.includes(userId)) ||
              (org.scanners && org.scanners.includes(userId))
            );
          });
        });
      }

      // Apply superAdmin include filter client-side if present (boolean comparison)
      if (hasSuperAdminIncludeFilter) {
        const superAdminValues = filters.include.superAdmin.map((v) => {
          if (v === "true" || v === true) return true;
          if (v === "false" || v === false) return false;
          return v;
        });
        visibleUsersArray = visibleUsersArray.filter((user) => {
          const userIsSuperAdmin = user.superAdmin === true;
          return superAdminValues.includes(userIsSuperAdmin);
        });
      }

      if (hasExcludeFilters) {
        Object.entries(filters.exclude || {}).forEach(([field, values]) => {
          if (values && values.length > 0) {
            visibleUsersArray = visibleUsersArray.filter((user) => {
              if (field === "organizationId") {
                // Check if user is a member of any excluded organization
                const userId = user.id;
                return !values.some((orgId) => {
                  const org = organizations[orgId];
                  if (!org) return false;
                  return (
                    org.owner === userId ||
                    (org.admins && org.admins.includes(userId)) ||
                    (org.scanners && org.scanners.includes(userId))
                  );
                });
              }

              let userValue = user[field];
              let filterValues = values;

              if (field === "superAdmin") {
                // Convert boolean to string for comparison
                userValue = userValue === true ? "true" : "false";
                filterValues = values.map((v) => {
                  if (v === true || v === "true") return "true";
                  if (v === false || v === "false") return "false";
                  return String(v);
                });
              } else {
                userValue = String(userValue || "");
              }
              return !filterValues.includes(userValue);
            });
          }
        });
      }

      // Convert to object keyed by user id
      const users = visibleUsersArray.reduce((acc, user) => {
        return {
          ...acc,
          [user.id]: user,
        };
      }, {});

      // For pagination, use the last VISIBLE user (after filtering), not the last fetched user
      const lastVisibleUser =
        visibleUsersArray.length > 0
          ? visibleUsersArray[visibleUsersArray.length - 1]
          : usersSnapshot.docs[usersSnapshot.docs.length - 1]?.data();

      let newLastVisible = null;
      if (lastVisibleUser) {
        const sortFieldValue = lastVisibleUser[currentSortBy];
        if (sortFieldValue !== undefined && sortFieldValue !== null) {
          newLastVisible = {
            [currentSortBy]: serialize(sortFieldValue),
          };
        }
      }

      // Mark as "all loaded" when we got fewer results from Firestore than requested
      const allUsersLoaded = usersSnapshot.docs.length < pageSize;

      logger.info("Fetched users", {
        count: usersArray.length,
        visibleCount: visibleUsersArray.length,
        allUsersLoaded,
      });

      return serialize({
        users,
        lastVisible: newLastVisible,
        allUsersLoaded,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
      });
    } catch (e) {
      logger.error("error fetching all users (paginated)", e);
      throw e;
    }
  },
);

export const fetchAllSquareAccessTokens = createAsyncThunk(
  "crm/fetchAllSquareAccessTokens",
  async (params, { getState, dispatch }) => {
    try {
      logger.info("Fetching all square access tokens");
      const tokensRef = collection(database, "squareAccessTokens");
      const q = query(tokensRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const tokens = querySnapshot.docs.reduce((acc, doc) => {
        return {
          ...acc,
          [doc.id]: { id: doc.id, ...doc.data() },
        };
      }, {});

      return serialize(tokens);
    } catch (e) {
      logger.error("error fetching all square access tokens", e);
      throw e;
    }
  },
);

export const fetchCrmStats = createAsyncThunk(
  "crm/fetchCrmStats",
  async (params, { getState, dispatch }) => {
    try {
      logger.info("Fetching CRM stats");
      const statsDocIds = ["organizationStats", "eventStats", "orderStats"];
      const stats = {};

      await Promise.all(
        statsDocIds.map(async (docId) => {
          const statsRef = doc(database, "crmStats", docId);
          const statsDoc = await getDoc(statsRef);
          if (statsDoc.exists()) {
            stats[docId] = { id: statsDoc.id, ...statsDoc.data() };
          } else {
            // Initialize with empty stats if document doesn't exist
            stats[docId] = { id: docId };
          }
        }),
      );

      return serialize(stats);
    } catch (e) {
      logger.error("error fetching CRM stats", e);
      throw e;
    }
  },
);

export const fetchOrganizationDetails = createAsyncThunk(
  "crm/fetchOrganizationDetails",
  async ({ organizationId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching organization details", { organizationId });
      const orgRef = doc(database, "organizations", organizationId);
      const orgDoc = await getDoc(orgRef);
      if (!orgDoc.exists()) {
        throw new Error("Organization not found");
      }
      const organization = { id: orgDoc.id, ...orgDoc.data() };

      // Collect all unique user IDs from the organization
      const userIdsSet = new Set();
      if (organization.owner) {
        userIdsSet.add(organization.owner);
      }
      if (organization.admins && Array.isArray(organization.admins)) {
        organization.admins.forEach((adminId) => userIdsSet.add(adminId));
      }
      if (organization.scanners && Array.isArray(organization.scanners)) {
        organization.scanners.forEach((scannerId) => userIdsSet.add(scannerId));
      }

      const allUserIds = Array.from(userIdsSet);

      // Check which users are already in the store
      const state = getState();
      const crmState = state.crm;
      const existingUsers = crmState.users || {};
      const existingUserIds = new Set(Object.keys(existingUsers));

      // Filter out users we already have
      const userIdsToFetch = allUserIds.filter(
        (id) => !existingUserIds.has(id),
      );

      logger.info("Fetching users for organization", {
        organizationId,
        total: allUserIds.length,
        existing: existingUserIds.size,
        toFetch: userIdsToFetch.length,
      });

      // Fetch users in batches (Firestore IN query limit is 10)
      const IN_QUERY_LIMIT = 10;
      const users = { ...existingUsers };

      if (userIdsToFetch.length > 0) {
        for (let i = 0; i < userIdsToFetch.length; i += IN_QUERY_LIMIT) {
          const batch = userIdsToFetch.slice(i, i + IN_QUERY_LIMIT);
          const usersRef = collection(database, "users");
          const userQuery = query(usersRef, where(documentId(), "in", batch));
          const userSnapshot = await getDocs(userQuery);
          userSnapshot.docs.forEach((doc) => {
            users[doc.id] = { id: doc.id, ...doc.data() };
          });
        }
      }

      return serialize({
        organizationId,
        organization,
        users,
      });
    } catch (e) {
      logger.error("error fetching organization details", e, { organizationId });
      throw e;
    }
  },
);

export const fetchOrganizationEvents = createAsyncThunk(
  "crm/fetchOrganizationEvents",
  async ({ organizationId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching organization events", { organizationId });
      const eventsRef = collection(database, "events");
      const q = query(eventsRef, where("organizationId", "==", organizationId));
      const querySnapshot = await getDocs(q);
      const eventsArray = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by createdAt descending in memory
      const sortedEvents = sortBy(eventsArray, (event) => {
        return -(event.createdAt?.toMillis?.() || 0);
      });

      const events = sortedEvents.reduce((acc, event) => {
        return {
          ...acc,
          [event.id]: event,
        };
      }, {});

      // Fetch eventStats for all events
      const eventIds = Object.keys(events);
      let eventStats = {};

      if (eventIds.length > 0) {
        const chunkSize = 30; // Firestore 'in' query limit is 30
        for (let i = 0; i < eventIds.length; i += chunkSize) {
          const chunk = eventIds.slice(i, i + chunkSize);
          const statsRef = collection(database, "eventStats");
          const statsQ = query(statsRef, where(documentId(), "in", chunk));
          const statsQuerySnapshot = await getDocs(statsQ);

          eventStats = statsQuerySnapshot.docs.reduce((acc, doc) => {
            return {
              ...acc,
              [doc.id]: { id: doc.id, ...doc.data() },
            };
          }, eventStats);
        }
      }

      return serialize({ organizationId, events, eventStats });
    } catch (e) {
      logger.error("error fetching organization events", e, { organizationId });
      throw e;
    }
  },
);

export const fetchOrganizationEventsTruncated = createAsyncThunk(
  "crm/fetchOrganizationEventsTruncated",
  async ({ organizationId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching truncated organization events", { organizationId });
      const eventsRef = collection(database, "events");
      const now = Timestamp.now();
      // Only fetch upcoming events (endsAt > now), limit to 10
      const q = query(
        eventsRef,
        where("organizationId", "==", organizationId),
        where("endsAt", ">", now),
        orderBy("endsAt", "asc"),
        limit(10),
      );
      const querySnapshot = await getDocs(q);
      const eventsArray = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const events = eventsArray.reduce((acc, event) => {
        return {
          ...acc,
          [event.id]: event,
        };
      }, {});

      // Fetch eventStats for the truncated events
      const eventIds = Object.keys(events);
      let eventStats = {};

      if (eventIds.length > 0) {
        const chunkSize = 30; // Firestore 'in' query limit is 30
        for (let i = 0; i < eventIds.length; i += chunkSize) {
          const chunk = eventIds.slice(i, i + chunkSize);
          const statsRef = collection(database, "eventStats");
          const statsQ = query(statsRef, where(documentId(), "in", chunk));
          const statsQuerySnapshot = await getDocs(statsQ);

          eventStats = statsQuerySnapshot.docs.reduce((acc, doc) => {
            return {
              ...acc,
              [doc.id]: { id: doc.id, ...doc.data() },
            };
          }, eventStats);
        }
      }

      return serialize({ organizationId, events, eventStats });
    } catch (e) {
      logger.error("error fetching truncated organization events", e, { organizationId });
      throw e;
    }
  },
);

export const fetchOrganizationOrders = createAsyncThunk(
  "crm/fetchOrganizationOrders",
  async ({ organizationId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching organization orders", { organizationId });
      const ordersRef = collection(
        database,
        "organizations",
        organizationId,
        "orders",
      );
      const q = query(ordersRef, orderBy("createdAt", "desc"), limit(100));
      const querySnapshot = await getDocs(q);
      const orders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return serialize({ organizationId, orders });
    } catch (e) {
      logger.error("error fetching organization orders", e, { organizationId });
      throw e;
    }
  },
);

export const fetchOrganizationOrdersTruncated = createAsyncThunk(
  "crm/fetchOrganizationOrdersTruncated",
  async ({ organizationId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching truncated organization orders", { organizationId });
      const ordersRef = collection(
        database,
        "organizations",
        organizationId,
        "orders",
      );
      const q = query(ordersRef, orderBy("createdAt", "desc"), limit(10));
      const querySnapshot = await getDocs(q);
      const orders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return serialize({ organizationId, orders });
    } catch (e) {
      logger.error("error fetching truncated organization orders", e, { organizationId });
      throw e;
    }
  },
);

export const fetchOrganizationSquareLocations = createAsyncThunk(
  "crm/fetchOrganizationSquareLocations",
  async ({ organizationId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching organization square locations", { organizationId });
      const locationsRef = collection(
        database,
        "organizations",
        organizationId,
        "squareLocations",
      );
      const q = query(locationsRef);
      const querySnapshot = await getDocs(q);
      const locations = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return serialize({ organizationId, locations });
    } catch (e) {
      logger.error("error fetching organization square locations", e, { organizationId });
      throw e;
    }
  },
);

export const fetchOrganizationSquareTokens = createAsyncThunk(
  "crm/fetchOrganizationSquareTokens",
  async ({ organizationId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching organization square tokens", { organizationId });
      const tokensRef = collection(database, "squareAccessTokens");
      const q = query(
        tokensRef,
        where("organizationId", "==", organizationId),
        orderBy("createdAt", "desc"),
      );
      const querySnapshot = await getDocs(q);
      const tokens = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return serialize({ organizationId, tokens });
    } catch (e) {
      logger.error("error fetching organization square tokens", e, { organizationId });
      throw e;
    }
  },
);

export const fetchUserDetails = createAsyncThunk(
  "crm/fetchUserDetails",
  async ({ userId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching user details", { userId });
      const userRef = doc(database, "users", userId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new Error("User not found");
      }
      const user = { id: userDoc.id, ...userDoc.data() };

      return serialize({ userId, user });
    } catch (e) {
      logger.error("error fetching user details", e, { userId });
      throw e;
    }
  },
);

export const fetchUserEvents = createAsyncThunk(
  "crm/fetchUserEvents",
  async ({ userId }, { getState, dispatch }) => {
    try {
      logger.info("Fetching user events", { userId });
      const eventsRef = collection(database, "events");

      // Firestore doesn't support or() with orderBy(), so we need to make two queries
      // and merge them, then sort in memory
      const adminQuery = query(
        eventsRef,
        where("admins", "array-contains", userId),
        orderBy("createdAt", "desc"),
        limit(10),
      );
      const scannerQuery = query(
        eventsRef,
        where("scanners", "array-contains", userId),
        orderBy("createdAt", "desc"),
        limit(10),
      );

      const [adminSnapshot, scannerSnapshot] = await Promise.all([
        getDocs(adminQuery),
        getDocs(scannerQuery),
      ]);

      // Merge events from both queries, deduplicating by event ID
      const events = {};
      adminSnapshot.docs.forEach((doc) => {
        events[doc.id] = { id: doc.id, ...doc.data() };
      });
      scannerSnapshot.docs.forEach((doc) => {
        events[doc.id] = { id: doc.id, ...doc.data() };
      });

      // Sort by createdAt descending and limit to 10
      const eventsList = Object.values(events);
      const sortedEvents = sortBy(eventsList, (event) => {
        return -(event.createdAt?.toMillis?.() || 0);
      }).slice(0, 10);

      const eventsMap = sortedEvents.reduce((acc, event) => {
        return {
          ...acc,
          [event.id]: event,
        };
      }, {});

      // Fetch event stats for these events
      const eventIds = Object.keys(eventsMap);
      let eventStats = {};

      if (eventIds.length > 0) {
        const chunkSize = 30; // Firestore 'in' query limit is 30
        for (let i = 0; i < eventIds.length; i += chunkSize) {
          const batch = eventIds.slice(i, i + chunkSize);
          const statsRef = collection(database, "eventStats");
          const statsQuery = query(statsRef, where(documentId(), "in", batch));
          const statsSnapshot = await getDocs(statsQuery);
          statsSnapshot.docs.forEach((doc) => {
            eventStats[doc.id] = { id: doc.id, ...doc.data() };
          });
        }
      }

      return serialize({ userId, events: eventsMap, eventStats });
    } catch (e) {
      logger.error("error fetching user events", e, { userId });
      throw e;
    }
  },
);

export const updateUserSuperAdmin = createAsyncThunk(
  "crm/updateUserSuperAdmin",
  async ({ userId, superAdmin, grantedByUserId }, { getState, dispatch }) => {
    try {
      logger.info("Updating user super admin status", {
        userId,
        superAdmin,
        grantedByUserId,
      });
      const userRef = doc(database, "users", userId);
      const updateData = {
        superAdmin,
        superAdminGrantedBy: superAdmin ? grantedByUserId : null,
      };
      await updateDoc(userRef, updateData);
      const userDoc = await getDoc(userRef);
      const user = { id: userDoc.id, ...userDoc.data() };
      return serialize({ userId, user });
    } catch (e) {
      logger.error("error updating user super admin status", e, { userId });
      throw e;
    }
  },
);

export const fetchAllEvents = createAsyncThunk(
  "crm/fetchAllEvents",
  async (params, { getState, dispatch }) => {
    try {
      const {
        filter = "upcoming",
        pageSize = 20,
        lastVisible,
        reset = false,
      } = params || {};
      logger.info("Fetching all events", {
        filter,
        pageSize,
        lastVisible,
        reset,
      });

      const eventsRef = collection(database, "events");
      let filterWhere = [];
      let orderByField = "createdAt";
      let orderByDirection = "desc";

      switch (filter) {
        case "upcoming":
          filterWhere = [where("endsAt", ">", Timestamp.now())];
          // Firestore requires orderBy on the same field as inequality filter
          orderByField = "endsAt";
          orderByDirection = "asc";
          break;
        case "draft":
          filterWhere = [where("publishedAt", "==", null)];
          break;
        case "past":
          filterWhere = [where("endsAt", "<", Timestamp.now())];
          orderByField = "endsAt";
          orderByDirection = "desc";
          break;
        default:
          filterWhere = [];
      }

      let eventsQuery;
      if (filterWhere.length > 0) {
        eventsQuery = query(
          eventsRef,
          and(...filterWhere),
          orderBy(orderByField, orderByDirection),
          limit(pageSize),
        );
      } else {
        eventsQuery = query(
          eventsRef,
          orderBy(orderByField, orderByDirection),
          limit(pageSize),
        );
      }

      if (lastVisible && !reset) {
        const lastVisibleTimestamp = deserialize(lastVisible);
        if (filterWhere.length > 0) {
          eventsQuery = query(
            eventsRef,
            and(...filterWhere),
            orderBy(orderByField, orderByDirection),
            startAfter(lastVisibleTimestamp),
            limit(pageSize),
          );
        } else {
          eventsQuery = query(
            eventsRef,
            orderBy(orderByField, orderByDirection),
            startAfter(lastVisibleTimestamp),
            limit(pageSize),
          );
        }
      }

      const eventsSnapshot = await getDocs(eventsQuery);
      const eventsArray = eventsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Convert events array to object keyed by event ID
      const events = eventsArray.reduce((acc, event) => {
        return {
          ...acc,
          [event.id]: event,
        };
      }, {});

      const lastVisibleEvent =
        eventsSnapshot.docs[eventsSnapshot.docs.length - 1];
      let newLastVisible = null;
      if (lastVisibleEvent) {
        const eventData = lastVisibleEvent.data();
        newLastVisible = serialize(eventData[orderByField]);
      }
      const allEventsLoaded = eventsSnapshot.docs.length < pageSize;

      // Fetch event stats for these events
      const eventIds = eventsArray.map((e) => e.id);
      let eventStats = {};

      if (eventIds.length > 0) {
        const chunkSize = 30;
        for (let i = 0; i < eventIds.length; i += chunkSize) {
          const chunk = eventIds.slice(i, i + chunkSize);
          const statsRef = collection(database, "eventStats");
          const statsQ = query(statsRef, where(documentId(), "in", chunk));
          const statsQuerySnapshot = await getDocs(statsQ);

          eventStats = statsQuerySnapshot.docs.reduce((acc, doc) => {
            const eventStat = { id: doc.id, ...doc.data() };
            return {
              ...acc,
              [doc.id]: eventStat,
            };
          }, eventStats);
        }
      }

      // Fetch event overrides and merge into events
      // Note: This requires superadmin access. If the user doesn't have permission, we'll skip overrides.
      let eventOverrides = {};
      if (eventIds.length > 0) {
        try {
          const chunkSize = 10; // Firestore 'in' queries are limited to 10
          for (let i = 0; i < eventIds.length; i += chunkSize) {
            const chunk = eventIds.slice(i, i + chunkSize);
            const overridesRef = collection(database, "eventOverrides");
            const overridesQ = query(
              overridesRef,
              where("eventId", "in", chunk),
            );
            const overridesQuerySnapshot = await getDocs(overridesQ);

            overridesQuerySnapshot.docs.forEach((doc) => {
              const override = { id: doc.id, ...doc.data() };
              if (override.eventId) {
                eventOverrides[override.eventId] = override;
              }
            });
          }
        } catch (error) {
          // If user doesn't have permission to read overrides, log and continue without them
          logger.warn(
            "Could not fetch event overrides (may require superadmin access)",
            error,
          );
        }
      }

      // Merge overrides into events
      Object.keys(events).forEach((eventId) => {
        const override = eventOverrides[eventId];
        if (override) {
          if (override.hideFromPublicLists !== undefined) {
            events[eventId].hideFromPublicLists = override.hideFromPublicLists;
          }
          if (override.hideEvent !== undefined) {
            events[eventId].hideEvent = override.hideEvent;
          }
        }
      });

      return {
        events: serialize(events),
        eventStats: serialize(eventStats),
        lastVisible: newLastVisible,
        allEventsLoaded,
        filter,
      };
    } catch (e) {
      logger.error("error fetching all events", e);
      throw e;
    }
  },
);

export const fetchAllOrders = createAsyncThunk(
  "crm/fetchAllOrders",
  async (params, { getState, dispatch }) => {
    try {
      const {
        pageSize = 50,
        lastVisible,
        reset = false,
        sortBy,
        sortOrder,
        filters = { include: {}, exclude: {} },
      } = params || {};
      logger.info("Fetching all orders", {
        pageSize,
        lastVisible,
        reset,
        sortBy,
        sortOrder,
        filters,
      });

      const state = getState();
      const crmState = state.crm;

      // Use provided sort params or fall back to state
      const currentSortBy =
        sortBy !== undefined ? sortBy : crmState.allOrdersSortBy || "createdAt";
      const currentSortOrder =
        sortOrder !== undefined
          ? sortOrder
          : crmState.allOrdersSortOrder || "desc";

      // If reset, start fresh
      if (reset) {
        // Will fetch from beginning
      } else if (crmState.allOrdersAllLoaded) {
        logger.info("All orders already loaded");
        return null;
      }

      const ordersRef = collection(database, "orders");

      // Build where conditions
      const whereConditions = [where("deleted", "==", false)];

      // Add include filters (field == value1 OR field == value2)
      Object.entries(filters.include || {}).forEach(([field, values]) => {
        if (values && values.length > 0) {
          // Convert values to appropriate types for Firestore
          let convertedValues = values;
          if (field === "totalUsdCents") {
            // Convert string cents to numbers
            convertedValues = values.map((v) => {
              const num = parseInt(v, 10);
              return isNaN(num) ? v : num;
            });
          }

          // Firestore doesn't support OR directly, so we use 'in' operator
          // Note: Firestore 'in' operator supports up to 10 values, but in practice
          // users can only select from visible filtered results, so this limit shouldn't be hit
          if (convertedValues.length === 1) {
            whereConditions.push(where(field, "==", convertedValues[0]));
          } else if (convertedValues.length <= 10) {
            whereConditions.push(where(field, "in", convertedValues));
          } else {
            // Fallback: If somehow we have more than 10 values (shouldn't happen in practice),
            // split into multiple queries would be needed, but for now just use first 10
            // This is a technical limitation that shouldn't be encountered in normal use
            whereConditions.push(
              where(field, "in", convertedValues.slice(0, 10)),
            );
          }
        }
      });

      let ordersQuery = query(
        ordersRef,
        ...whereConditions,
        orderBy(currentSortBy, currentSortOrder),
        limit(pageSize),
      );

      if (lastVisible && !reset) {
        // Get the last visible value for the current sort field
        const lastVisibleValue = lastVisible[currentSortBy];
        if (lastVisibleValue !== undefined && lastVisibleValue !== null) {
          let startAfterValue = lastVisibleValue;

          // Convert timestamp if needed
          if (
            currentSortBy === "createdAt" &&
            lastVisibleValue.seconds !== undefined
          ) {
            startAfterValue = new Timestamp(
              lastVisibleValue.seconds,
              lastVisibleValue.nanoseconds || 0,
            );
          }

          ordersQuery = query(
            ordersRef,
            ...whereConditions,
            orderBy(currentSortBy, currentSortOrder),
            startAfter(startAfterValue),
            limit(pageSize),
          );
        }
      }

      const ordersSnapshot = await getDocs(ordersQuery);
      let ordersArray = ordersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Apply exclude filters client-side (Firestore limitation with != and orderBy)
      let visibleOrdersArray = ordersArray;
      const hasExcludeFilters =
        filters.exclude && Object.keys(filters.exclude).length > 0;

      if (hasExcludeFilters) {
        Object.entries(filters.exclude || {}).forEach(([field, values]) => {
          if (values && values.length > 0) {
            visibleOrdersArray = visibleOrdersArray.filter((order) => {
              let orderValue = order[field];
              let filterValues = values;

              if (field === "totalUsdCents") {
                orderValue =
                  typeof orderValue === "number"
                    ? orderValue
                    : parseInt(orderValue, 10);
                filterValues = values
                  .map((v) => parseInt(v, 10))
                  .filter((v) => !isNaN(v));
              } else {
                orderValue = String(orderValue || "");
              }
              return !filterValues.includes(orderValue);
            });
          }
        });
      }

      // Convert to object keyed by order id
      const orders = visibleOrdersArray.reduce((acc, order) => {
        return {
          ...acc,
          [order.id]: order,
        };
      }, {});

      // For pagination, use the last VISIBLE order (after filtering), not the last fetched order
      // This ensures we don't skip valid results when paginating
      const lastVisibleOrder =
        visibleOrdersArray.length > 0
          ? visibleOrdersArray[visibleOrdersArray.length - 1]
          : ordersSnapshot.docs[ordersSnapshot.docs.length - 1]?.data();

      let newLastVisible = null;
      if (lastVisibleOrder) {
        const sortFieldValue = lastVisibleOrder[currentSortBy];
        if (sortFieldValue !== undefined && sortFieldValue !== null) {
          newLastVisible = {
            [currentSortBy]: serialize(sortFieldValue),
          };
        }
      }

      // Mark as "all loaded" when:
      // 1. We got fewer results from Firestore than requested (no more data), OR
      // 2. We have exclude filters AND we got fewer results from Firestore (can't fetch more to filter)
      const gotFewerFromFirestore = ordersSnapshot.docs.length < pageSize;
      const allOrdersLoaded = gotFewerFromFirestore;

      logger.info("Fetched orders", {
        count: ordersArray.length,
        allOrdersLoaded,
      });

      return serialize({
        orders,
        lastVisible: newLastVisible,
        allOrdersLoaded,
      });
    } catch (e) {
      logger.error("error fetching all orders", e);
      throw e;
    }
  },
);

const organizationDetailsRawSelector = (state) => state.crm.organizationDetails;
const organizationDetailsLoadingSelector = (state) =>
  state.crm.organizationDetailsLoading;
const organizationEventsRawSelector = (state) => state.crm.organizationEvents;
const organizationEventsLoadingSelector = (state) =>
  state.crm.organizationEventsLoading;
const organizationEventStatsRawSelector = (state) =>
  state.crm.organizationEventStats;
const organizationEventsTruncatedRawSelector = (state) =>
  state.crm.organizationEventsTruncated;
const organizationEventsTruncatedLoadingSelector = (state) =>
  state.crm.organizationEventsTruncatedLoading;
const organizationEventStatsTruncatedRawSelector = (state) =>
  state.crm.organizationEventStatsTruncated;
const organizationOrdersTruncatedRawSelector = (state) =>
  state.crm.organizationOrdersTruncated;
const organizationOrdersTruncatedLoadingSelector = (state) =>
  state.crm.organizationOrdersTruncatedLoading;
const organizationOrdersRawSelector = (state) => state.crm.organizationOrders;
const organizationOrdersLoadingSelector = (state) =>
  state.crm.organizationOrdersLoading;
const organizationSquareLocationsRawSelector = (state) =>
  state.crm.organizationSquareLocations;
const organizationSquareLocationsLoadingSelector = (state) =>
  state.crm.organizationSquareLocationsLoading;
const organizationSquareTokensRawSelector = (state) =>
  state.crm.organizationSquareTokens;
const organizationSquareTokensLoadingSelector = (state) =>
  state.crm.organizationSquareTokensLoading;
const crmStatsRawSelector = (state) => state.crm.crmStats;
const crmStatsLoadingSelector = (state) => state.crm.crmStatsLoading;
const userDetailsRawSelector = (state) => state.crm.userDetails;
const userDetailsLoadingSelector = (state) => state.crm.userDetailsLoading;
const userEventsRawSelector = (state) => state.crm.userEvents;
const userEventsLoadingSelector = (state) => state.crm.userEventsLoading;
const userEventStatsRawSelector = (state) => state.crm.userEventStats || {};

// Deserializing selectors
const organizationDetailsSelector = createSelector(
  [organizationDetailsRawSelector],
  (details) => {
    const deserialized = {};
    Object.keys(details || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(details[orgId]);
    });
    return deserialized;
  },
);

const organizationEventsSelector = createSelector(
  [organizationEventsRawSelector],
  (events) => {
    const deserialized = {};
    Object.keys(events || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(events[orgId]);
    });
    return deserialized;
  },
);

// Helper function to enhance ticket types with statuses
const enhanceTicketTypesWithStatuses = (ticketTypes) => {
  if (!ticketTypes || !Array.isArray(ticketTypes)) {
    return [];
  }
  const nowTimestamp = Timestamp.now();
  return ticketTypes.map((ticketType) => {
    let { availabilityStartsAt, availabilityEndsAt } = ticketType;
    let status;

    // Ensure availabilityStartsAt and availabilityEndsAt are proper Timestamps
    // They should be deserialized by the selector, but handle both cases
    if (availabilityStartsAt && !availabilityStartsAt.toMillis) {
      if (availabilityStartsAt.seconds !== undefined) {
        availabilityStartsAt = new Timestamp(
          availabilityStartsAt.seconds,
          availabilityStartsAt.nanoseconds || 0,
        );
      }
    }
    if (availabilityEndsAt && !availabilityEndsAt.toMillis) {
      if (availabilityEndsAt.seconds !== undefined) {
        availabilityEndsAt = new Timestamp(
          availabilityEndsAt.seconds,
          availabilityEndsAt.nanoseconds || 0,
        );
      }
    }

    // Note: We don't have ticket type stats here, so we can't check for soldOut
    // We'll only determine pending/active/ended based on timestamps
    const nowMillis = nowTimestamp.toMillis();
    const startsAtMillis = availabilityStartsAt?.toMillis?.() || null;
    const endsAtMillis = availabilityEndsAt?.toMillis?.() || null;

    if (startsAtMillis && startsAtMillis > nowMillis) {
      status = "pending";
    } else if (endsAtMillis && endsAtMillis < nowMillis) {
      status = "ended";
    } else {
      status = "active";
    }
    return {
      ...ticketType,
      status,
      availabilityStartsAt,
      availabilityEndsAt,
    };
  });
};

// Enhanced selector that adds ticket type statuses and publicUrl to events
const organizationEventsWithEnhancedTicketTypesSelector = createSelector(
  [organizationEventsSelector],
  (events) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const enhanced = {};
    Object.keys(events || {}).forEach((orgId) => {
      const orgEvents = events[orgId] || {};
      enhanced[orgId] = {};
      Object.keys(orgEvents).forEach((eventId) => {
        const event = orgEvents[eventId];
        const { name } = event;
        const publicUrl = `${baseUrl}/events/${getSlug(
          name,
        )}-tickets-${eventId}`;
        enhanced[orgId][eventId] = {
          ...event,
          ticketTypes: enhanceTicketTypesWithStatuses(event.ticketTypes),
          publicUrl,
        };
      });
    });
    return enhanced;
  },
);

const organizationEventStatsSelector = createSelector(
  [organizationEventStatsRawSelector],
  (eventStats) => {
    const deserialized = {};
    Object.keys(eventStats || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(eventStats[orgId]);
    });
    return deserialized;
  },
);

const organizationEventsTruncatedSelector = createSelector(
  [organizationEventsTruncatedRawSelector],
  (events) => {
    const deserialized = {};
    Object.keys(events || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(events[orgId]);
    });
    return deserialized;
  },
);

// Enhanced selector that adds ticket type statuses and publicUrl to truncated events
const organizationEventsTruncatedWithEnhancedTicketTypesSelector =
  createSelector([organizationEventsTruncatedSelector], (events) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const enhanced = {};
    Object.keys(events || {}).forEach((orgId) => {
      const orgEvents = events[orgId] || {};
      enhanced[orgId] = {};
      Object.keys(orgEvents).forEach((eventId) => {
        const event = orgEvents[eventId];
        const { name } = event;
        const publicUrl = `${baseUrl}/events/${getSlug(
          name,
        )}-tickets-${eventId}`;
        enhanced[orgId][eventId] = {
          ...event,
          ticketTypes: enhanceTicketTypesWithStatuses(event.ticketTypes),
          publicUrl,
        };
      });
    });
    return enhanced;
  });

const organizationEventStatsTruncatedSelector = createSelector(
  [organizationEventStatsTruncatedRawSelector],
  (eventStats) => {
    const deserialized = {};
    Object.keys(eventStats || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(eventStats[orgId]);
    });
    return deserialized;
  },
);

const organizationOrdersTruncatedSelector = createSelector(
  [organizationOrdersTruncatedRawSelector],
  (orders) => {
    const deserialized = {};
    Object.keys(orders || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(orders[orgId]);
    });
    return deserialized;
  },
);

const organizationOrdersSelector = createSelector(
  [organizationOrdersRawSelector],
  (orders) => {
    const deserialized = {};
    Object.keys(orders || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(orders[orgId]);
    });
    return deserialized;
  },
);

const organizationSquareLocationsSelector = createSelector(
  [organizationSquareLocationsRawSelector],
  (locations) => {
    const deserialized = {};
    Object.keys(locations || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(locations[orgId]);
    });
    return deserialized;
  },
);

const organizationSquareTokensSelector = createSelector(
  [organizationSquareTokensRawSelector],
  (tokens) => {
    const deserialized = {};
    Object.keys(tokens || {}).forEach((orgId) => {
      deserialized[orgId] = deserialize(tokens[orgId]);
    });
    return deserialized;
  },
);

const crmStatsSelector = createSelector([crmStatsRawSelector], (stats) =>
  deserialize(stats || {}),
);

const userDetailsSelector = createSelector(
  [userDetailsRawSelector],
  (details) => {
    const deserialized = {};
    Object.keys(details || {}).forEach((userId) => {
      deserialized[userId] = deserialize(details[userId]);
    });
    return deserialized;
  },
);

const userEventsSelector = createSelector([userEventsRawSelector], (events) => {
  const deserialized = {};
  Object.keys(events || {}).forEach((userId) => {
    deserialized[userId] = deserialize(events[userId]);
  });
  return deserialized;
});

// Enhanced selector that adds ticket type statuses and publicUrl to user events
const userEventsWithEnhancedTicketTypesSelector = createSelector(
  [userEventsSelector],
  (events) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const enhanced = {};
    Object.keys(events || {}).forEach((userId) => {
      const userEventsMap = events[userId] || {};
      enhanced[userId] = {};
      Object.keys(userEventsMap).forEach((eventId) => {
        const event = userEventsMap[eventId];
        const { name } = event;
        const publicUrl = `${baseUrl}/events/${getSlug(
          name,
        )}-tickets-${eventId}`;
        enhanced[userId][eventId] = {
          ...event,
          ticketTypes: enhanceTicketTypesWithStatuses(event.ticketTypes),
          publicUrl,
        };
      });
    });
    return enhanced;
  },
);

const userEventStatsSelector = createSelector(
  [userEventStatsRawSelector],
  (eventStats) => {
    const deserialized = {};
    Object.keys(eventStats || {}).forEach((userId) => {
      deserialized[userId] = deserialize(eventStats[userId]);
    });
    return deserialized;
  },
);

const allEventsRawSelector = (state) => state.crm.allEvents;
const allEventsLoadingSelector = (state) => state.crm.allEventsLoading;
const allEventsLastVisibleSelector = (state) => state.crm.allEventsLastVisible;
const allEventsAllLoadedSelector = (state) => state.crm.allEventsAllLoaded;
const allEventsFilterSelector = (state) => state.crm.allEventsFilter;
const allEventStatsRawSelector = (state) => state.crm.allEventStats || {};

const allEventsSelector = createSelector([allEventsRawSelector], (events) =>
  deserialize(events || {}),
);

// Enhanced selector that adds ticket type statuses to all events
const allEventsWithEnhancedTicketTypesSelector = createSelector(
  [allEventsSelector],
  (events) => {
    const enhanced = {};
    Object.keys(events || {}).forEach((eventId) => {
      const event = events[eventId];
      enhanced[eventId] = {
        ...event,
        ticketTypes: enhanceTicketTypesWithStatuses(event.ticketTypes),
      };
    });
    return enhanced;
  },
);

const allEventStatsSelector = createSelector(
  [allEventStatsRawSelector],
  (stats) => deserialize(stats || {}),
);

const allOrdersRawSelector = (state) => state.crm.allOrders;
const allOrdersLoadingSelector = (state) => state.crm.allOrdersLoading;
const allOrdersLastVisibleSelector = (state) => state.crm.allOrdersLastVisible;
const allOrdersAllLoadedSelector = (state) => state.crm.allOrdersAllLoaded;

const allUsersRawSelector = (state) => state.crm.allUsers;
const allUsersLoadingSelector = (state) => state.crm.allUsersLoading;
const allUsersLastVisibleSelector = (state) => state.crm.allUsersLastVisible;
const allUsersAllLoadedSelector = (state) => state.crm.allUsersAllLoaded;
const allUsersSortBySelector = (state) => state.crm.allUsersSortBy;
const allUsersSortOrderSelector = (state) => state.crm.allUsersSortOrder;
const allOrdersSortBySelector = (state) => state.crm.allOrdersSortBy;
const allOrdersSortOrderSelector = (state) => state.crm.allOrdersSortOrder;

const allOrdersSelector = createSelector([allOrdersRawSelector], (orders) =>
  deserialize(orders || {}),
);

// Convert orders object to list (Firestore already returns them sorted)
const allOrdersListSelector = createSelector([allOrdersSelector], (orders) => {
  return Object.values(orders || {});
});

const allUsersSelector = createSelector([allUsersRawSelector], (users) =>
  deserialize(users || {}),
);

// Convert users object to list and enrich with organizations (Firestore already returns them sorted)
const allUsersListSelector = createSelector(
  [allUsersSelector, organizationsSelector],
  (users, organizations) => {
    const userList = Object.values(users || {}).map((user) => {
      const userId = user.id;

      // Find all organizations this user is part of and their roles
      const orgMemberships = [];
      Object.values(organizations || {}).forEach((org) => {
        const roles = [];
        if (org.owner === userId) {
          roles.push("Owner");
        }
        if (
          org.admins &&
          Array.isArray(org.admins) &&
          org.admins.includes(userId)
        ) {
          roles.push("Admin");
        }
        if (
          org.scanners &&
          Array.isArray(org.scanners) &&
          org.scanners.includes(userId)
        ) {
          roles.push("Scanner");
        }

        if (roles.length > 0) {
          orgMemberships.push({
            organizationId: org.id,
            organizationName: org.name || "Unnamed Organization",
            roles,
          });
        }
      });

      return {
        ...user,
        organizations: orgMemberships,
      };
    });

    return userList;
  },
);

export const crmSliceSelector = createStructuredSelector({
  updatingAllOrgLocations: updatingAllOrgLocationsSelector,
  updateAllOrgLocationsResult: updateAllOrgLocationsResultSelector,
  updateAllOrgLocationsError: updateAllOrgLocationsErrorSelector,
  resyncingAllOrders: resyncingAllOrdersSelector,
  resyncAllOrdersResult: resyncAllOrdersResultSelector,
  resyncAllOrdersError: resyncAllOrdersErrorSelector,
  backfillingAttendees: backfillingAttendeesSelector,
  backfillAttendeesResult: backfillAttendeesResultSelector,
  backfillAttendeesError: backfillAttendeesErrorSelector,
  organizations: organizationsListSelector,
  organizationsMap: organizationsSelector,
  organizationsRaw: organizationsRawSelector,
  organizationsLoading: organizationsLoadingSelector,
  organizationsFetched: organizationsFetchedSelector,
  users: usersListSelector,
  usersMap: usersSelector,
  usersRaw: usersRawSelector,
  usersLoading: usersLoadingSelector,
  usersFetched: usersFetchedSelector,
  squareAccessTokens: squareAccessTokensListSelector,
  squareAccessTokensMap: squareAccessTokensSelector,
  squareAccessTokensRaw: squareAccessTokensRawSelector,
  squareAccessTokensLoading: squareAccessTokensLoadingSelector,
  squareAccessTokensFetched: squareAccessTokensFetchedSelector,
  organizationDetails: organizationDetailsSelector,
  organizationDetailsLoading: organizationDetailsLoadingSelector,
  organizationEvents: organizationEventsSelector,
  organizationEventsWithEnhancedTicketTypes:
    organizationEventsWithEnhancedTicketTypesSelector,
  organizationEventsLoading: organizationEventsLoadingSelector,
  organizationEventStats: organizationEventStatsSelector,
  organizationEventsTruncated: organizationEventsTruncatedSelector,
  organizationEventsTruncatedWithEnhancedTicketTypes:
    organizationEventsTruncatedWithEnhancedTicketTypesSelector,
  organizationEventsTruncatedLoading:
    organizationEventsTruncatedLoadingSelector,
  organizationEventStatsTruncated: organizationEventStatsTruncatedSelector,
  organizationOrders: organizationOrdersSelector,
  organizationOrdersLoading: organizationOrdersLoadingSelector,
  organizationOrdersTruncated: organizationOrdersTruncatedSelector,
  organizationOrdersTruncatedLoading:
    organizationOrdersTruncatedLoadingSelector,
  organizationSquareLocations: organizationSquareLocationsSelector,
  organizationSquareLocationsLoading:
    organizationSquareLocationsLoadingSelector,
  organizationSquareTokens: organizationSquareTokensSelector,
  organizationSquareTokensLoading: organizationSquareTokensLoadingSelector,
  crmStats: crmStatsSelector,
  crmStatsLoading: crmStatsLoadingSelector,
  userDetails: userDetailsSelector,
  userDetailsLoading: userDetailsLoadingSelector,
  userEvents: userEventsSelector,
  userEventsWithEnhancedTicketTypes: userEventsWithEnhancedTicketTypesSelector,
  userEventsLoading: userEventsLoadingSelector,
  userEventStats: userEventStatsSelector,
  allEvents: allEventsWithEnhancedTicketTypesSelector,
  allEventsLoading: allEventsLoadingSelector,
  allEventsLastVisible: allEventsLastVisibleSelector,
  allEventsAllLoaded: allEventsAllLoadedSelector,
  allEventsFilter: allEventsFilterSelector,
  allEventStats: allEventStatsSelector,
  allOrders: allOrdersListSelector,
  allOrdersLoading: allOrdersLoadingSelector,
  allOrdersLastVisible: allOrdersLastVisibleSelector,
  allOrdersAllLoaded: allOrdersAllLoadedSelector,
  allOrdersSortBy: allOrdersSortBySelector,
  allOrdersSortOrder: allOrdersSortOrderSelector,
  allUsers: allUsersListSelector,
  allUsersLoading: allUsersLoadingSelector,
  allUsersLastVisible: allUsersLastVisibleSelector,
  allUsersAllLoaded: allUsersAllLoadedSelector,
  allUsersSortBy: allUsersSortBySelector,
  allUsersSortOrder: allUsersSortOrderSelector,
});

export default crmSlice.reducer;
export const {
  clearUpdateAllOrgLocationsResult,
  clearResyncAllOrdersResult,
  clearBackfillAttendeesResult,
  updateEventOverrides: updateEventOverridesInState,
} = crmSlice.actions;
